import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import verticalDarkLogo from "../../assets/images/vertical_dark.png";
import { DEFAULT_GRAZING_DAYS } from "./constants";
import type {
  AnimalGroupBudget,
  AnimalGroupComputedRow,
  FeedBudgetPlan,
  StockClassComputedMonth,
} from "./types";
import { MONTH_LABELS, MONTH_LABELS_FULL } from "./types";

interface Props {
  open: boolean;
  onClose: () => void;
  budget: AnimalGroupBudget;
  groupRows: AnimalGroupComputedRow[];
  stockClassComputed: Map<string, StockClassComputedMonth[]>;
  grazingMonths: FeedBudgetPlan["grazingMonths"];
  planName: string;
  planYear?: number;
  defaultMonth: number;
}

function buildPrintHtml(
  budget: AnimalGroupBudget,
  month: number,
  groupRow: AnimalGroupComputedRow | undefined,
  stockClassComputed: Map<string, StockClassComputedMonth[]>,
  grazingMonths: FeedBudgetPlan["grazingMonths"],
  planName: string,
  planYear?: number,
  logoUrl?: string
): string {
  const monthName = MONTH_LABELS_FULL[month];
  const daysInMonth = DEFAULT_GRAZING_DAYS[month] || 30;
  const year = planYear ?? new Date().getFullYear();

  // Brand: Dahlia palette
  const c900 = "#1C4321";
  const c700 = "#2E7831";
  const c500 = "#7EC254";
  const c300 = "#AFE57F";
  const c100 = "#DBF4C2";
  const c50 = "#F7FCF1";

  const prevMonth = (month + 11) % 12;
  const nextMonth = (month + 1) % 12;

  function getBiologicalStage(
    defaultClass?: string,
    allocPct?: number
  ): string {
    switch (defaultClass) {
      case "DAIRY_COW":
        return allocPct !== undefined && allocPct <= 2.1
          ? "Dry Period"
          : "Lactating";
      case "REPLACEMENT_HEIFER":
        return "Growing";
      case "BREEDING_COW":
        if (allocPct !== undefined) {
          if (allocPct <= 1.6) return "Dry / Post-Weaning";
          if (allocPct <= 2.1) return "Pre-Calving";
          if (allocPct <= 2.6) return "Early Lactation";
          return "Peak Lactation";
        }
        return "Breeding Cow";
      case "GROWING_CATTLE":
        return "Growing / Store";
      case "BREEDING_EWE":
        if (allocPct !== undefined) {
          if (allocPct <= 1.6) return "Dry / Post-Weaning";
          if (allocPct <= 2.1) return "Pre-Lambing";
          return "Lactating";
        }
        return "Breeding Ewe";
      case "GROWING_LAMB":
        return "Growing";
      case "BULL":
        return "Maintenance";
      case "RAM":
        return "Maintenance";
      default:
        return "—";
    }
  }

  function getTargetStatus(
    defaultClass?: string,
    allocPct?: number,
    weightKg?: number
  ): string {
    if (allocPct === undefined) return "—";
    switch (defaultClass) {
      case "BULL":
      case "RAM":
        return "Maintain BCS";
      case "BREEDING_COW":
      case "BREEDING_EWE":
      case "DAIRY_COW":
        return `${allocPct.toFixed(1)}% BW/day`;
      case "REPLACEMENT_HEIFER":
      case "GROWING_CATTLE":
      case "GROWING_LAMB":
        return `${allocPct.toFixed(1)}% BW/day`;
      default:
        return `${allocPct.toFixed(1)}% BW/day`;
    }
  }

  const stockRows = budget.stockClassPlans.map((sc) => {
    const computed = stockClassComputed.get(sc.stockClassId);
    const cm = computed?.find((m) => m.month === month);
    const prevCm = computed?.find((m) => m.month === prevMonth);
    const nextCm = computed?.find((m) => m.month === nextMonth);
    const entry = sc.months.find((m) => m.month === month);
    return {
      name: sc.stockClassName,
      defaultClass: sc.defaultStockClassName,
      count: cm?.animalCount ?? 0,
      allocPct: cm?.allocationPercent,
      weightKg: cm?.averageWeightKg,
      dailyPerHead: cm?.dailyAllocationKgDM ?? 0,
      totalDaily: cm?.totalDailyRequirementKgDM ?? 0,
      monthlyTotal: (cm?.totalDailyRequirementKgDM ?? 0) * daysInMonth,
      prevCount: prevCm?.animalCount ?? 0,
      nextCount: nextCm?.animalCount ?? 0,
      isGrazing: entry?.isGrazing !== false,
    };
  });

  const activeStockRows = stockRows.filter((r) => r.count > 0);

  // Detect key events this month from count transitions
  const keyEvents: string[] = [];
  stockRows.forEach((r) => {
    if (r.prevCount === 0 && r.count > 0) {
      keyEvents.push(`${r.count} ${r.name} joining this month`);
    }
    const endOfMonthDrop = r.count - r.nextCount;
    if (r.count > 0 && r.nextCount === 0) {
      keyEvents.push(`${r.count} ${r.name} leaving end of month`);
    } else if (endOfMonthDrop > 0 && r.nextCount > 0) {
      keyEvents.push(`${endOfMonthDrop} ${r.name} leaving end of month`);
    }
  });

  const totalMonthlyKgDM = Math.round(
    activeStockRows.reduce((sum, r) => sum + r.monthlyTotal, 0)
  );

  const monthlySupplPlan = budget.supplementPlans.find(
    (s) => s.month === month
  );

  // Supplements: total planned amounts for this month
  // Utilisation: when grazing → fieldFeedingUtilisationPercent; when housed → feedTypeUtilisation per type
  const supplements = budget.supplementFeedTypes
    .map((ft) => {
      const monthlyT = monthlySupplPlan?.amounts?.[ft.id] ?? 0;
      const dailyKgOffered = (monthlyT * 1000) / daysInMonth;
      return { id: ft.id, name: ft.name, dailyKgOffered };
    })
    .filter((r) => r.dailyKgOffered > 0);

  const totalGroupDemand = activeStockRows.reduce(
    (sum, r) => sum + r.totalDaily,
    0
  );
  const totalHeadcount = activeStockRows.reduce((sum, r) => sum + r.count, 0);

  // Grass supply from the app's computed row (matches the Supply panel exactly)
  const area = groupRow?.grazingAreaHa ?? budget.grazingAreaHa;
  const totalGrassKgDay = (groupRow?.supplyKgHaDay ?? 0) * area;

  // Raw growth before utilisation loss — shown as "Offered" for grazing
  const grazingConfig = grazingMonths.find((gm) => gm.month === month);
  const rawGrowthKgHaDay = grazingConfig?.growthRateKgHaDay ?? 0;
  const utilisationPct = grazingConfig?.utilisationPercent ?? 65;
  const totalRawGrassKgDay = rawGrowthKgHaDay * area;

  // Build card HTML for each stock class
  const stockCards = activeStockRows
    .map((r) => {
      const share = totalGroupDemand > 0 ? r.totalDaily / totalGroupDemand : 0;

      // Utilisation: field rate when grazing, per-type rate when housed
      const getUtilPct = (ftId: string) =>
        r.isGrazing
          ? budget.fieldFeedingUtilisationPercent ?? 100
          : budget.feedTypeUtilisation?.[ftId] ?? 100;

      // Effective = offered × utilisation × substitution rate (matches supply panel formula)
      const getSubstitutionRate = (ftId: string) =>
        budget.supplementFeedTypes.find((ft) => ft.id === ftId)
          ?.substitutionRate ?? 1;

      const suppTableRows = supplements
        .map((s) => {
          const utilPct = getUtilPct(s.id);
          const subRate = getSubstitutionRate(s.id);
          const offeredPerHead =
            r.count > 0 ? (s.dailyKgOffered * share) / r.count : 0;
          const effectivePerHead = offeredPerHead * (utilPct / 100) * subRate;
          const totalDailyEffective = effectivePerHead * r.count;
          const monthlyEffective = totalDailyEffective * daysInMonth;
          return `<tr>
            <td>${s.name}</td>
            <td style="text-align:right">${offeredPerHead.toFixed(1)}</td>
            <td style="text-align:right">${effectivePerHead.toFixed(1)}</td>
            <td style="text-align:right">${Math.round(
              totalDailyEffective
            ).toLocaleString()}</td>
            <td style="text-align:right"><strong>${Math.round(
              monthlyEffective
            ).toLocaleString()}</strong></td>
          </tr>`;
        })
        .join("");

      // Grazing from supply panel data, distributed by demand share
      const grazingTotalKgDay = totalGrassKgDay * share;
      const grazingPerHead = r.count > 0 ? grazingTotalKgDay / r.count : 0;
      const rawGrazingTotalKgDay = totalRawGrassKgDay * share;
      const rawGrazingPerHead =
        r.count > 0 ? rawGrazingTotalKgDay / r.count : 0;

      const suppSection =
        supplements.length > 0 || grazingPerHead > 0.05
          ? `
        <table class="supp-table">
          <thead>
            <tr>
              <th>Feed Source</th>
              <th style="text-align:right">Offered<br><span style="font-weight:400;text-transform:none">(kgDM/head/day)</span></th>
              <th style="text-align:right">Effective<br><span style="font-weight:400;text-transform:none">(kgDM/head/day)</span></th>
              <th style="text-align:right">Total for Group<br><span style="font-weight:400;text-transform:none">(kgDM/day)</span></th>
              <th style="text-align:right">Monthly Total<br><span style="font-weight:400;text-transform:none">(kgDM)</span></th>
            </tr>
          </thead>
          <tbody>
            ${suppTableRows}
            ${
              grazingPerHead > 0.05
                ? `<tr class="grazing-row">
              <td>Grazing <span style="font-size:7.5pt;font-weight:400">(${utilisationPct}% utilisation)</span></td>
              <td style="text-align:right">${rawGrazingPerHead.toFixed(1)}</td>
              <td style="text-align:right">${grazingPerHead.toFixed(1)}</td>
              <td style="text-align:right">${Math.round(
                grazingTotalKgDay
              ).toLocaleString()}</td>
              <td style="text-align:right"><strong>${Math.round(
                grazingTotalKgDay * daysInMonth
              ).toLocaleString()}</strong></td>
            </tr>`
                : ""
            }
          </tbody>
        </table>`
          : "";

      return `
      <div class="sc-card">
        <div class="sc-header">
          <div class="sc-name">${r.name}</div>
          <div class="sc-meta">
            <span class="sc-pill">${r.count} head</span>
            <span class="sc-pill">${getBiologicalStage(
              r.defaultClass,
              r.allocPct
            )}</span>
            <span class="sc-pill">${getTargetStatus(
              r.defaultClass,
              r.allocPct,
              r.weightKg
            )}</span>
            <span class="sc-demand">Total feed demand: <strong>${r.dailyPerHead.toFixed(
              1
            )} kgDM/head/day</strong></span>
          </div>
        </div>
        ${suppSection}
      </div>`;
    })
    .join("");

  const keyEventsHtml = keyEvents
    .map((e) => `<div class="event-item">• ${e}</div>`)
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Monthly Feed Plan — ${monthName}</title>
  <style>
    @page { size: A4 landscape; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 10pt; color: ${c900}; padding: 1.2cm 1.5cm; }

    /* Header */
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
    .header-farm { font-size: 15pt; font-weight: 700; color: ${c900}; }
    .header-month { font-size: 10pt; color: ${c700}; margin-top: 2px; }

    /* Key events box */
    .top-banner { background: ${c50}; border: 1.5px solid ${c300}; border-radius: 8px; padding: 12px 16px; }
    .banner-events-label { font-size: 8pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: ${c700}; margin-bottom: 4px; }
    .event-item { font-size: 9pt; color: ${c900}; line-height: 1.6; }

    /* Stock class cards */
    .sc-card { border: 1px solid ${c300}; border-radius: 6px; margin-bottom: 14px; overflow: hidden; }
    .sc-header { padding: 10px 14px; border-bottom: 1px solid ${c100}; }
    .sc-name { font-size: 11pt; font-weight: 700; color: ${c900}; }
    .sc-meta { display: flex; align-items: center; gap: 12px; margin-top: 4px; flex-wrap: wrap; }
    .sc-pill { font-size: 8.5pt; color: ${c700}; }
    .sc-pill + .sc-pill::before { content: "·"; margin-right: 12px; color: ${c300}; }
    .sc-demand { font-size: 9pt; color: ${c900}; margin-left: auto; }

    /* Supplement tables inside cards */
    .supp-table { width: 100%; border-collapse: collapse; font-size: 9pt; }
    .supp-table th { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: ${c700}; padding: 5px 14px; text-align: left; border-bottom: 1px solid ${c300}; }
    .supp-table td { padding: 5px 14px; border-bottom: 1px solid #e5e7eb; color: ${c900}; }
    .supp-table tr:last-child td { border-bottom: none; }
    .grazing-row td { color: ${c700}; font-style: italic; border-top: 1px solid ${c300}; }

    /* Section titles */
    .section-title { font-size: 8pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: ${c700}; margin: 22px 0 8px; border-bottom: 1.5px solid ${c300}; padding-bottom: 3px; }

    /* Notes */
    .notes-line { border-bottom: 1px solid ${c300}; height: 28px; margin-bottom: 2px; }

  </style>
</head>
<body>

  <div class="header">
    <div>
      <div class="header-farm">Feed Plan: ${budget.animalGroupName}</div>
      <div class="header-month">${monthName} ${year} (${planName})</div>
    </div>
    ${
      logoUrl
        ? `<img src="${logoUrl}" alt="ruumi" style="width:72px;display:block;margin-top:-8px" />`
        : ""
    }
  </div>

  ${
    activeStockRows.length > 0
      ? stockCards
      : `<p style="color:#888;font-style:italic;padding:10px 0">No stock classes with animals this month</p>`
  }

  ${
    keyEventsHtml
      ? `
  <div class="top-banner" style="margin-top:22px;margin-bottom:0">
    <div class="banner-events" style="text-align:left">
      <div class="banner-events-label">Key Events This Month</div>
      ${keyEventsHtml}
    </div>
  </div>`
      : ""
  }

  <div class="section-title">Notes</div>
  ${Array.from({ length: 4 }, () => '<div class="notes-line"></div>').join("")}


</body>
</html>`;
}

export default function FeedingSheetPrintDialog({
  open,
  onClose,
  budget,
  groupRows,
  stockClassComputed,
  grazingMonths,
  planName,
  planYear,
  defaultMonth,
}: Props) {
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const previewRef = useRef<HTMLIFrameElement>(null);

  const groupRow = groupRows.find((r) => r.month === selectedMonth);
  // Bundled asset (same pattern as manual-draw.png); resolve absolute URL so
  // print/popup HTML with about:blank still loads the image on staging BASE_URL paths.
  const logoUrl = new URL(verticalDarkLogo, window.location.href).href;

  const handlePrint = () => {
    const html = buildPrintHtml(
      budget,
      selectedMonth,
      groupRow,
      stockClassComputed,
      grazingMonths,
      planName,
      planYear,
      logoUrl
    );
    const win = window.open("", "_blank", "width=1024,height=768");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => {
      win.focus();
      win.print();
    };
  };

  const previewHtml = buildPrintHtml(
    budget,
    selectedMonth,
    groupRow,
    stockClassComputed,
    grazingMonths,
    planName,
    planYear,
    logoUrl
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Print Feeding Sheet
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={selectedMonth}
            label="Month"
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {MONTH_LABELS.map((m, i) => (
              <MenuItem key={i} value={i}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: "hidden" }}>
        <Box
          sx={{
            height: 520,
            border: "1px solid #e5e7eb",
            mx: 2,
            mb: 1,
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <iframe
            ref={previewRef}
            title="Feeding sheet preview"
            srcDoc={previewHtml}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
